/* Pipeline (landing) — ported from the prototype's Screens.pipeline().
   Board (Kanban across the five stages) + List view, traffic-lit scores,
   Board/List toggle, and "+ Add applicant". Cards open the deal at its
   current step. Deals load live from GET /deal/pipeline. */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { BASE, scoreClass, defaultStep } from '../helpers';
import { STAGES } from '../data/config';
import { useToast } from '../components/Toast';
import { useProfile } from '../state';
import { Loading, EmptyState, ErrorState } from '../components/States';
import AddApplicantModal from '../components/AddApplicantModal';
import { fetchPipelineRes, seedDemo, ok } from '../api';

function KanbanBoard({ deals, navigate }) {
	return (
		<div className="kanban">
			{STAGES.map(stage => {
				const stageDeals = deals.filter(d => d.stage === stage.id);
				return (
					<div className="kcol" key={stage.id}>
						<div className="kcol__head">
							<span className="kcol__dot" style={{ background: stage.dot }} />
							{stage.name}
							<span className="kcol__count">{stageDeals.length}</span>
						</div>
						{stageDeals.map(d => (
							<div key={d.id} className={`kcard ${d.focus ? 'is-focus' : ''}`}
								onClick={() => navigate(`${BASE}/deal/${d.id}/${defaultStep(d)}`)}>
								<div className="kcard__top">
									<span className="kcard__name">{d.name}</span>
									{d.score != null
										? <span className={`kcard__score score ${scoreClass(d.score)}`}>{d.score}</span>
										: <span className="kcard__score" style={{ color: 'var(--dlg-axis)' }}>—</span>}
								</div>
								<div className="kcard__meta">{d.sector} · {d.ask}</div>
								<div className="kcard__status">{d.status}</div>
							</div>
						))}
					</div>
				);
			})}
		</div>
	);
}

function PipelineList({ deals, navigate }) {
	const stageName = id => { const s = STAGES.find(x => x.id === id); return s ? s.name : id; };
	return (
		<div className="listview listwrap">
			<table>
				<thead>
					<tr><th>Company</th><th>Sector</th><th>Ask</th><th>Stage</th><th>Score</th><th>Status</th></tr>
				</thead>
				<tbody>
					{deals.map(d => (
						<tr key={d.id} onClick={() => navigate(`${BASE}/deal/${d.id}/${defaultStep(d)}`)}>
							<td style={{ fontWeight: 500 }}>{d.name}</td>
							<td>{d.sector}</td>
							<td>{d.ask}</td>
							<td><span className="chip chip--blue">{stageName(d.stage)}</span></td>
							<td className={`score ${scoreClass(d.score)}`}>{d.score != null ? d.score : '—'}</td>
							<td style={{ color: 'var(--dlg-muted)' }}>{d.status}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

export default function Pipeline({ view = 'board' }) {
	const showToast = useToast();
	const navigate = useNavigate();
	const { profile, reload: reloadProfile } = useProfile();
	const [deals, setDeals] = useState(null);
	const [error, setError] = useState(false);
	const [errMsg, setErrMsg] = useState('');
	const [seeding, setSeeding] = useState(false);
	const [addOpen, setAddOpen] = useState(false);

	const load = useCallback(async () => {
		setError(false);
		const res = await fetchPipelineRes();
		if (!res) { setError(true); setErrMsg('No response from the server — it may be restarting. Try again in a moment.'); setDeals(null); return; }
		if (res.code === 401) { setError(true); setErrMsg('Your session has expired — please log in again.'); setDeals(null); return; }
		if (res.code === 403) { setError(true); setErrMsg('This account is not an investor account, so Deal Management is unavailable.'); setDeals(null); return; }
		if (/no investor firm/i.test(res.message || '')) { setError(true); setErrMsg('This account has no investor fund attached. Deal Management is only available on an investor account with a fund — sign in with your investor account.'); setDeals(null); return; }
		if (res.code !== 200 || !res.body) { setError(true); setErrMsg(res.message || 'Could not load the pipeline.'); setDeals(null); return; }
		setDeals(res.body.deals || []);
	}, []);

	useEffect(() => { load(); }, [load]);

	// Dev convenience: populate this firm with the worked example so the flow is walkable.
	const loadDemo = async () => {
		if (seeding) return;
		setSeeding(true);
		const res = await seedDemo();
		setSeeding(false);
		if (!ok(res)) return;
		showToast('Demo data loaded for your firm');
		await Promise.all([load(), reloadProfile()]);
	};

	return (
		<main className="page screen">
			<div className="page__head">
				<div>
					<div className="kicker">Deal flow · {(profile && profile.short) || 'Your fund'}</div>
					<div className="page__title">Pipeline</div>
				</div>
				<div className="page__actions">
					<div className="seg" role="tablist">
						<button className={view === 'board' ? 'is-active' : ''} onClick={() => navigate(`${BASE}/pipeline`)}>Board</button>
						<button className={view === 'list' ? 'is-active' : ''} onClick={() => navigate(`${BASE}/pipeline/list`)}>List</button>
					</div>
					<button className="pill pill--primary" onClick={() => setAddOpen(true)}>
						+ Add applicant
					</button>
				</div>
			</div>

			{error ? <ErrorState title="Could not load the pipeline" sub={errMsg || 'Please try again.'} onRetry={load} />
				: deals === null ? <Loading label="Loading pipeline…" />
					: deals.length === 0 ? (
						<div className="stub">
							<div className="stub__title">No deals yet</div>
							<div>New applicants arrive from your funding form and appear here.</div>
							<button className="pill pill--primary" style={{ marginTop: 14 }} onClick={loadDemo} disabled={seeding}>{seeding ? 'Loading…' : 'Load demo data'}</button>
						</div>
					)
						: (
							<>
								{view === 'board' ? <KanbanBoard deals={deals} navigate={navigate} /> : <PipelineList deals={deals} navigate={navigate} />}
								<p className="kanban__legend">Drag deals between stages, or let the workflow move them for you. Scores are traffic-lit:{' '}
									<span className="score score--green">70+</span> · <span className="score score--amber">50–69</span> · <span className="score score--red">below 50</span>.
								</p>
							</>
						)}

			{addOpen && <AddApplicantModal onClose={() => setAddOpen(false)} onAdded={load} />}
		</main>
	);
}
